import { Download, Loader2 } from "lucide-react";
import { Badge } from "../ui/badge";

export const ORDER_STATUS_STYLES = {
  ORDER_CREATED: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
  CONFIRMED: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30",
  PARTNER_ASSIGNED: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30",
  ACCEPTED: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/30",
  IN_PRODUCTION: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30",
  PRODUCTION_COMPLETE: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  ON_HOLD: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
  CANCELLED: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
  QUALITY_PENDING: "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/30",
  INSPECTION_IN_PROGRESS: "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/30",
  QUALITY_PASSED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  QUALITY_FAILED: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
  REWORK_REQUIRED: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30",
  READY_FOR_DISPATCH: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/30",
  DISPATCHED: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/30",
  IN_TRANSIT: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/30",
  DELIVERED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  DELIVERY_CONFIRMED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
};

export const ORDER_STAGES = ["ORDER_CREATED", "CONFIRMED", "PARTNER_ASSIGNED", "ACCEPTED", "IN_PRODUCTION", "PRODUCTION_COMPLETE",
  "QUALITY_PENDING", "INSPECTION_IN_PROGRESS", "QUALITY_PASSED", "READY_FOR_DISPATCH", "DISPATCHED", "IN_TRANSIT", "DELIVERED", "DELIVERY_CONFIRMED"];
const STAGE_ALIAS = { QUALITY_FAILED: "INSPECTION_IN_PROGRESS", REWORK_REQUIRED: "IN_PRODUCTION" };
export const QUALITY_STATES = ["QUALITY_PENDING", "INSPECTION_IN_PROGRESS", "QUALITY_PASSED", "QUALITY_FAILED", "REWORK_REQUIRED"];
export const DELIVERY_STATES = ["READY_FOR_DISPATCH", "DISPATCHED", "IN_TRANSIT", "DELIVERED", "DELIVERY_CONFIRMED"];
export const ACTIVE_ORDER = (s) => !["DELIVERY_CONFIRMED", "CANCELLED"].includes(s);
export const SHIP_TO_LOCKED = (s) => ["DISPATCHED", "IN_TRANSIT", "DELIVERED", "DELIVERY_CONFIRMED", "CANCELLED"].includes(s);

export const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
export const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export const OrderStatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${ORDER_STATUS_STYLES[status] || ORDER_STATUS_STYLES.ORDER_CREATED}`} data-testid={`order-status-badge-${status?.toLowerCase()}`}>
    {status?.replace(/_/g, " ")}
  </span>
);

export const StageTrack = ({ status, heldFrom }) => {
  const raw = status === "ON_HOLD" ? heldFrom : status;
  const current = STAGE_ALIAS[raw] || raw;
  const idx = ORDER_STAGES.indexOf(current);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="order-stage-track">
      {ORDER_STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-1">
          <span title={s} className={`h-2 w-2 rounded-full transition-colors duration-300 ${status === "CANCELLED" ? "bg-red-500/40" : i <= idx ? "bg-primary" : "bg-border"}`} />
          {i < ORDER_STAGES.length - 1 && <span className={`h-px w-2 sm:w-4 ${i < idx && status !== "CANCELLED" ? "bg-primary" : "bg-border"}`} />}
        </div>
      ))}
      <span className="ml-3 text-xs text-muted-foreground font-mono-tech">{idx >= 0 ? `${idx + 1}/${ORDER_STAGES.length}` : "—"}{STAGE_ALIAS[raw] ? ` · ${raw.replace(/_/g, " ").toLowerCase()}` : ""}</span>
    </div>
  );
};

export const FileChip = ({ file, onDownload, downloading, testId }) => (
  <button onClick={() => onDownload(file)} disabled={downloading === file.id} data-testid={testId}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary hover:bg-primary/20 transition-colors duration-200">
    {downloading === file.id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <Download className="h-3 w-3" strokeWidth={1.5} />}
    {file.name}
  </button>
);

export const OrderParts = ({ order, onDownload, downloading, testPrefix }) => (
  <div className="mt-4 space-y-2">
    {order.parts.map((p, i) => (
      <div key={i} className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-lg bg-muted/40 border border-border text-sm" data-testid={`${testPrefix}-part-${order.id}-${i}`}>
        <span className="font-medium">{p.name}</span>
        <Badge variant="secondary">{p.process}</Badge>
        <Badge variant="outline">{p.material}{p.grade ? ` · ${p.grade}` : ""}</Badge>
        <span className="text-xs text-muted-foreground">× {p.quantity}</span>
        {p.notes && <span className="text-xs text-muted-foreground whitespace-pre-line">— {p.notes}</span>}
        {onDownload && (
          <span className="ml-auto flex gap-2 flex-wrap">
            {(p.files || []).map((f) => <FileChip key={f.id} file={f} onDownload={onDownload} downloading={downloading} testId={`${testPrefix}-file-${f.id}`} />)}
          </span>
        )}
      </div>
    ))}
  </div>
);

export const ProgressList = ({ order, onDownload, downloading, testPrefix }) => {
  if (!order.progress?.length) return null;
  return (
    <div className="mt-4 space-y-2" data-testid={`${testPrefix}-progress-${order.id}`}>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Production updates</p>
      {order.progress.map((u) => (
        <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-lg border border-border text-sm">
          <Badge variant="secondary">{u.stage.replace(/_/g, " ")}</Badge>
          <span className="font-mono-tech font-bold text-primary">{u.percent}%</span>
          <div className="h-1.5 w-24 rounded-full bg-border overflow-hidden"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${u.percent}%` }} /></div>
          {u.note && <span className="text-muted-foreground">{u.note}</span>}
          <span className="ml-auto text-xs text-muted-foreground">{fmtDate(u.at)}</span>
          {onDownload && (u.files || []).map((f) => <FileChip key={f.id} file={f} onDownload={onDownload} downloading={downloading} testId={`${testPrefix}-progress-file-${f.id}`} />)}
        </div>
      ))}
    </div>
  );
};

export const OrderTimeline = ({ order }) => (
  <div className="mt-4 pt-3 border-t border-border flex flex-wrap gap-x-4 gap-y-1" data-testid={`order-timeline-${order.id}`}>
    {order.timeline.map((t, i) => (
      <span key={i} className="text-xs text-muted-foreground">
        <b>{t.status.replace(/_/g, " ")}</b> · {t.by} · {fmtDate(t.ts)}{t.note ? ` · ${t.note}` : ""}
      </span>
    ))}
  </div>
);
